"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DateRangePickerInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _isAfter = _interopRequireDefault(require("date-fns/isAfter"));
var _isBefore = _interopRequireDefault(require("date-fns/isBefore"));
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../i18n/i18n");
var _useTheme = require("../../../theme/utils/useTheme");
var _formlabel = require("../../formlabel");
var _visuallyhidden = require("../../visuallyhidden");
var _DateRangePickerDialog = require("../dialog/components/DateRangePickerDialog");
var _useDateRangePickerDialog = require("../dialog/hooks/useDateRangePickerDialog");
var _DatePickerCatalogs = require("../i18n/DatePickerCatalogs");
var _calendar = require("../util/calendar");
var _AtomicDatePickerInput = require("./components/AtomicDatePickerInput");
var _DateRangePickerInputWrapper = require("./styled/DateRangePickerInputWrapper");
var _RangeInputsLabelBetween = require("./styled/RangeInputsLabelBetween");
var _utils = require("./utils");
const getErrorMessage = (i18n, format, result, beforeAfterVal) => {
  if (result === "isValid") {
    return "";
  }
  const messages = {
    isInvalid: i18n._( /*i18n*/
    {
      id: "Please enter a valid date ({format})",
      values: {
        format
      },
      comment: "An error message showing the user has entered a date in the wrong format. It shows the correct format in parenthesis."
    }),
    isBefore: i18n._( /*i18n*/{
      id: "Please select a date after {minDate}",
      values: {
        minDate: beforeAfterVal
      },
      comment: "An error message showing the user has entered a date before the required minimum date."
    }),
    isAfter: i18n._( /*i18n*/{
      id: "Please select a date before {maxDate}",
      values: {
        maxDate: beforeAfterVal
      },
      comment: "An error message showing the user has entered a date after the required maximum date."
    }),
    isDisabled: i18n._( /*i18n*/{
      id: "The selected date is unavailable. Please select another.",
      comment: "An error message showing the user has entered a date that is not allowed because it is disabled."
    }),
    isRequired: i18n._( /*i18n*/{
      id: "Please enter a valid date ({format})",
      values: {
        format
      },
      comment: "An error message telling the user they need to enter a date since it is required. It shows the correct format in parenthesis."
    })
  };
  return messages[result];
};
const DateRangePickerInputBase = /*#__PURE__*/(0, _react.forwardRef)((_ref, containerRef) => {
  let {
    className,
    clientId,
    dayIsDisabled,
    endValue,
    format,
    id: providedInputId,
    i18n,
    label = "",
    locale,
    maxDate,
    minDate,
    isRequired,
    onStartDateValidationStateChange,
    onEndDateValidationStateChange,
    onChange,
    startValue,
    initialPickerDate,
    portalProps,
    dialogPopperProps,
    inputFieldHeight
  } = _ref;
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const generatedStartDateInputId = (0, _react.useMemo)(() => providedInputId || (0, _lodestarUtils.genId)("dp"), [providedInputId]);
  const generatedRootLabelId = (0, _react.useMemo)(() => (0, _lodestarUtils.genId)("dp-label"), []);
  const startDateLabelId = (0, _react.useMemo)(() => (0, _lodestarUtils.genId)("dp-start-label"), []);
  const endDatelabelId = (0, _react.useMemo)(() => (0, _lodestarUtils.genId)("dp-end-label"), []);
  const [startValidation, setStartValidation] = (0, _react.useState)(null);
  const [endValidation, setEndValidation] = (0, _react.useState)(null);
  const [startDate, setStartDate] = (0, _react.useState)(startValue);
  const [endDate, setEndDate] = (0, _react.useState)(endValue);
  const [pickerIsOpen, setPickerIsOpen] = (0, _react.useState)(false);
  const startCalendarButtonRef = (0, _react.useRef)(null);
  const endCalendarButtonRef = (0, _react.useRef)(null);
  const targetPickerButton = (0, _react.useRef)(null);

  // If the user changes the value, ensure it's passed along.
  (0, _react.useEffect)(() => {
    setStartDate(startValue);
    setEndDate(endValue);
  }, [startValue, endValue]);
  const openPicker = target => {
    setPickerIsOpen(true);

    // Track the button that opened this dialog so we can
    // refocus it when the dialog closes.
    if (target) {
      targetPickerButton.current = target;
    }
  };
  const closePicker = refocus => {
    setPickerIsOpen(false);
    if (targetPickerButton.current && refocus) {
      requestAnimationFrame(() => {
        targetPickerButton.current?.focus();
        targetPickerButton.current = null;
      });
    }
  };
  const onPickerChange = values => {
    let date1 = null;
    let date2 = null;
    if (values) {
      date1 = values[0];
      date2 = values[1];
    }
    setStartDate(date1);
    setEndDate(date2);
    onChange(date1, date2);
    closePicker(true);
  };
  const onStartDateChange = value => {
    setStartDate(value);
    onChange(value, endDate);
  };
  const onEndDateChange = value => {
    setEndDate(value);
    onChange(startDate, value);
  };

  //TODO Cleanup and DRY up
  const onStartValidationChange = (result, sVal) => {
    if (result !== "isBefore" && result !== "isAfter") {
      setStartValidation(result === "isValid" ? null : getErrorMessage(i18n, format, result));
    } else if (result === "isAfter") {
      let afterDate = (0, _utils.dateToInputString)(endDate, format);
      if (maxDate && endDate) {
        afterDate = (0, _isBefore.default)((0, _calendar.toDate)(endDate), (0, _calendar.toDate)(maxDate)) ? (0, _utils.dateToInputString)(endDate, format) : (0, _utils.dateToInputString)(maxDate, format);
      } else if (maxDate) {
        afterDate = (0, _utils.dateToInputString)(maxDate, format);
      } else {
        afterDate = (0, _utils.dateToInputString)(endDate, format);
      }
      const eMess = getErrorMessage(i18n, format, result, afterDate);
      setStartValidation(eMess);
    } else if (result === "isBefore" && sVal) {
      let afterDate = sVal;
      if (minDate) {
        afterDate = (0, _isAfter.default)(new Date(sVal), (0, _calendar.toDate)(minDate)) ? sVal : (0, _utils.dateToInputString)(minDate, format);
      }
      const eMess = getErrorMessage(i18n, format, result, afterDate);
      setStartValidation(eMess);
    }
    onStartDateValidationStateChange?.(result);
  };
  const onEndValidationChange = (result, eVal) => {
    setEndValidation(result === "isValid" ? null : getErrorMessage(i18n, format, result));
    if (result === "isBefore") {
      let beforeDate = (0, _utils.dateToInputString)(startDate, format);
      if (minDate && startDate) {
        beforeDate = (0, _isAfter.default)((0, _calendar.toDate)(startDate), (0, _calendar.toDate)(minDate)) ? (0, _utils.dateToInputString)(startDate, format) : (0, _utils.dateToInputString)(minDate, format);
      } else if (minDate) {
        beforeDate = (0, _utils.dateToInputString)(minDate, format);
      } else {
        beforeDate = (0, _utils.dateToInputString)(startDate, format);
      }
      const eMess = getErrorMessage(i18n, format, result, beforeDate);
      setEndValidation(eMess);
    }
    if (result === "isAfter" && eVal) {
      let beforeDate = eVal;
      if (maxDate) {
        beforeDate = (0, _isBefore.default)(new Date(eVal), (0, _calendar.toDate)(maxDate)) ? eVal : (0, _utils.dateToInputString)(maxDate, format);
      }
      const eMess = getErrorMessage(i18n, format, result, beforeDate);
      setEndValidation(eMess);
    }
    onEndDateValidationStateChange?.(result);
  };
  const {
    getTargetHTMLProps,
    getPickerProps
  } = (0, _useDateRangePickerDialog.useDateRangePickerDialog)({
    clientId: (0, _lodestarUtils.getClientId)(clientId, "dp-dialog"),
    dayIsDisabled,
    initialPickerDate,
    isOpen: pickerIsOpen,
    locale,
    maxDate,
    minDate,
    onChange: onPickerChange,
    onClear: () => {
      onPickerChange(null);
    },
    onCloseRequested: closePicker,
    onOpenRequested: openPicker,
    placement: "bottom-start",
    portalProps,
    // We'll manage the focus ourselves.
    refocusTargetOnClose: false,
    value: getInitialDialogValue(startDate, endDate, initialPickerDate)
  });
  const firstInputRef = (0, _react.useRef)(null);
  const {
    ref: theTargetRef,
    ...targetProps
  } = getTargetHTMLProps({});
  const pickerFlyoutTargetRef = (0, _lodestarUtils.useForkRef)(firstInputRef, theTargetRef);
  return /*#__PURE__*/_react.default.createElement(_formlabel.FormLabel, {
    className: className,
    "data-client-id": (0, _lodestarUtils.getClientId)(clientId, "dpri-root"),
    ref: containerRef,
    label: label || "",
    hideLabel: !label,
    validationState: startValidation || endValidation ? "error" : undefined,
    validationMessage: startValidation || endValidation || undefined,
    id: generatedStartDateInputId,
    labelProps: {
      id: generatedRootLabelId
    }
  }, _ref2 => {
    let {
      "aria-labelledby": overallLabelId,
      id: startDateInputId,
      ...restLabelProps
    } = _ref2;
    return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_visuallyhidden.VisuallyHidden, {
      id: startDateLabelId,
      "aria-hidden": "true"
    }, i18n._( /*i18n*/{
      id: "Start Date",
      comment: "The label for the start date input."
    })), /*#__PURE__*/_react.default.createElement(_visuallyhidden.VisuallyHidden, {
      id: endDatelabelId,
      "aria-hidden": "true"
    }, i18n._( /*i18n*/{
      id: "End Date",
      comment: "The label for the end date input."
    })), /*#__PURE__*/_react.default.createElement(_DateRangePickerInputWrapper.DateRangePickerInputWrapper, null, /*#__PURE__*/_react.default.createElement(_AtomicDatePickerInput.AtomicDatePickerInput, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight,
      "aria-labelledby": `${label ? overallLabelId : ""} ${startDateLabelId}`
    }, restLabelProps, {
      calendarButtonProps: {
        ...targetProps,
        ref: startCalendarButtonRef
      },
      clientId: (0, _lodestarUtils.getClientId)(clientId, "dpri-startdate-input"),
      format: format,
      id: startDateInputId,
      maxDate: endDate ? endDate : maxDate,
      minDate: minDate,
      isRequired: isRequired,
      onChange: onStartDateChange,
      onValidationChange: onStartValidationChange,
      ref: pickerFlyoutTargetRef,
      value: startDate
    })), /*#__PURE__*/_react.default.createElement(_RangeInputsLabelBetween.RangeInputsLabelBetween, {
      themeMode: themeMode
    }, i18n._( /*i18n*/{
      id: "to",
      comment: "A text separator between 2 HTML inputs that contain dates. e.g. 1/1/2020 to 1/2/2020."
    })), /*#__PURE__*/_react.default.createElement(_AtomicDatePickerInput.AtomicDatePickerInput, (0, _extends2.default)({
      inputFieldHeight: inputFieldHeight,
      "aria-labelledby": `${label ? overallLabelId : ""} ${endDatelabelId}`
    }, restLabelProps, {
      calendarButtonProps: {
        ...targetProps,
        onClick: e => openPicker(e.currentTarget),
        ref: endCalendarButtonRef
      },
      clientId: (0, _lodestarUtils.getClientId)(clientId, "dpri-enddate-input"),
      format: format,
      maxDate: maxDate,
      minDate: startDate ? startDate : minDate,
      isRequired: isRequired,
      onChange: onEndDateChange,
      onValidationChange: onEndValidationChange,
      value: endDate
    }))), /*#__PURE__*/_react.default.createElement(_DateRangePickerDialog.DateRangePickerDialog, (0, _extends2.default)({}, getPickerProps(), {
      onCloseRequested: closePicker,
      popperProps: dialogPopperProps
    })));
  });
});
DateRangePickerInputBase.displayName = "DateRangePickerInputBase";
function getInitialDialogValue(start, end, initialPickerDate) {
  const startDate = start || initialPickerDate || (0, _calendar.toDatePickerDate)((0, _calendar.getToday)());
  const endDate = end ? end : startDate;
  return [startDate, endDate];
}
const DateRangePickerInput = /*#__PURE__*/(0, _react.forwardRef)((_ref3, ref) => {
  let {
    ...props
  } = _ref3;
  (0, _DatePickerCatalogs.injectCatalog)();
  return /*#__PURE__*/_react.default.createElement(_i18n.I18nContextProvider, {
    locale: props.locale
  }, /*#__PURE__*/_react.default.createElement(_i18n.I18NContextConsumer, null, _ref4 => {
    let {
      i18n
    } = _ref4;
    return /*#__PURE__*/_react.default.createElement(DateRangePickerInputBase, (0, _extends2.default)({}, props, {
      i18n: i18n,
      ref: ref
    }));
  }));
});
exports.DateRangePickerInput = DateRangePickerInput;
DateRangePickerInput.displayName = "DateRangePickerInput";
//# sourceMappingURL=DateRangePickerInput.js.map